import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Share2, Bookmark, Zap, Activity, Target, Check, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useNavigate } from 'react-router-dom';
import ReactDOM from 'react-dom';
import { useNoshStore } from '../lib/store';
import { toast } from '../components/ui/use-toast';

interface NutritionData {
  calories: number;
  protein: number;
  carbs: number;
  fat: number;
  fiber: number;
  sugar: number;
  sodium: number;
  servingSize: string;
  mealName: string;
  ingredients: string[];
  confidence: number;
  ai_suggestions?: string[];
}

interface UserProfile {
  dailyCalorieGoal: number;
  currentIntake: number;
  age: number;
  gender: 'male' | 'female';
  height: number; // cm
  weight: number; // kg
  activityLevel: 'sedentary' | 'lightly_active' | 'moderately_active' | 'very_active';
  goal: 'maintain' | 'lose' | 'gain';
}

const NutritionAnalysis = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [nutritionData, setNutritionData] = useState<NutritionData | null>(null);
  const [mealImage, setMealImage] = useState<string>("/food.jpg");
  const [permanentImagePath, setPermanentImagePath] = useState<string | null>(null);
  const [hasRealData, setHasRealData] = useState(false);

  // Get addMeal function from store
  const { addMeal } = useNoshStore();

  // Mock user profile data - this should come from user store/database
  const userProfile: UserProfile = {
    dailyCalorieGoal: 2100, // Calculated based on user's profile
    currentIntake: 1240, // Already consumed today
    age: 28,
    gender: 'female',
    height: 165,
    weight: 60,
    activityLevel: 'moderately_active',
    goal: 'maintain'
  };

  // Load meal data from sessionStorage or use mock data
  useEffect(() => {
    const loadMealData = () => {
      try {
        const storedData = sessionStorage.getItem('mealAnalysisData');
        console.log('📱 [Mobile] Loading meal data from sessionStorage...');
        
        if (storedData) {
          const data = JSON.parse(storedData);
          console.log('📊 [Mobile] Found stored meal data:', data);
          
          // Check if data is still valid (within 1 hour)
          const dataAge = Date.now() - new Date(data.timestamp).getTime();
          const maxAge = 60 * 60 * 1000; // 1 hour
          
          if (dataAge < maxAge && data.mealData) {
            // Convert AI response to our interface format
            const mealData = data.mealData;
            const convertedData: NutritionData = {
              calories: mealData.estimated_calories || mealData.calories || 385,
              protein: mealData.macros?.protein || mealData.protein || 28,
              carbs: mealData.macros?.carbs || mealData.carbs || 18,
              fat: mealData.macros?.fat || mealData.fat || 24,
              fiber: mealData.nutrition?.fiber || mealData.fiber || 12,
              sugar: mealData.nutrition?.sugar || mealData.sugar || 8,
              sodium: mealData.nutrition?.sodium || mealData.sodium || 420,
              servingSize: mealData.serving_size || "1 bowl (280g)",
              mealName: mealData.meal_name || "AI Analyzed Meal",
              ingredients: mealData.ingredients || ["Various ingredients"],
              confidence: mealData.confidence || 85,
              ai_suggestions: mealData.ai_suggestions || []
            };
            
            setNutritionData(convertedData);
            setMealImage(data.imageUrl);
            
            // Store permanent image path if available
            if (data.imagePath) {
              setPermanentImagePath(data.imagePath);
              console.log('📂 [Mobile] Stored permanent image path:', data.imagePath);
            }
            
            console.log('✅ [Mobile] Loaded real meal analysis data:', convertedData);
          } else {
            console.warn('⚠️ [Mobile] Stored data is too old or invalid, using fallback');
            setNutritionData(mockNutritionData);
          }
        } else {
          console.warn('⚠️ [Mobile] No stored meal data found, using fallback');
          setNutritionData(mockNutritionData);
        }
      } catch (error) {
        console.error('❌ [Mobile] Error loading meal data:', error);
        setNutritionData(mockNutritionData);
      }
    };

    loadMealData();
  }, []);

  // Mock data fallback
  const mockNutritionData: NutritionData = {
    calories: 385,
    protein: 28,
    carbs: 18,
    fat: 24,
    fiber: 12,
    sugar: 8,
    sodium: 420,
    servingSize: "1 bowl (280g)",
    mealName: "Grilled Chicken & Avocado Salad Bowl",
    ingredients: ["Grilled chicken breast", "Fresh avocado", "Cherry tomatoes", "Mixed greens", "Cucumber", "Lettuce", "Olive oil", "Lemon juice"],
    confidence: 94
  };

  // Calculate daily intake progress - now using the loaded data
  const newIntake = userProfile.currentIntake + (nutritionData?.calories || 0);
  const intakePercentage = Math.min((newIntake / userProfile.dailyCalorieGoal) * 100, 100);
  const remainingCalories = userProfile.dailyCalorieGoal - newIntake;

  const handleSaveMeal = async () => {
    setIsSaving(true);
    
    try {
      if (nutritionData) {
        console.log('💾 [Mobile] Saving meal to database...', {
          mealName: nutritionData.mealName,
          imageUrl: permanentImagePath || mealImage,
          calories: nutritionData.calories
        });
        
        // Save meal data to database using the same logic as desktop modal
        await addMeal({
          mealName: nutritionData.mealName,
          imageUrl: permanentImagePath || mealImage, // Use permanent storage path if available
          servingSize: nutritionData.servingSize,
          confidence: nutritionData.confidence,
          calories: nutritionData.calories,
          protein: nutritionData.protein,
          carbs: nutritionData.carbs,
          fat: nutritionData.fat,
          fiber: nutritionData.fiber,
          sugar: nutritionData.sugar,
          sodium: nutritionData.sodium,
          ingredients: nutritionData.ingredients,
          aiSuggestions: nutritionData.ai_suggestions || []
        });
        
        console.log('✅ [Mobile] Meal saved successfully to database');
        
        // Show success toast
        toast({
          title: "Meal Saved! 🍽️",
          description: `${nutritionData.mealName} has been added to your meal tracker.`,
        });
        
        // Navigate back after successful save
        setTimeout(() => {
          navigate(-1);
        }, 1000); // Give user time to see the success message
      }
    } catch (error) {
      console.error('❌ [Mobile] Failed to save meal:', error);
      
      // Show error toast
      toast({
        title: "Save Failed",
        description: "Could not save meal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => setIsLoading(false), 2000);
    return () => clearTimeout(timer);
  }, []);

  // Hide mobile navigation when this page is active
  useEffect(() => {
    document.body.classList.add('nutrition-analysis');
    
    return () => {
      document.body.classList.remove('nutrition-analysis');
    };
  }, []);

  const microData = [
    { label: "Fiber", value: nutritionData?.fiber || 0, unit: "g", icon: "🌾" },
    { label: "Sugar", value: nutritionData?.sugar || 0, unit: "g", icon: "🍯" },
    { label: "Sodium", value: nutritionData?.sodium || 0, unit: "mg", icon: "🧂" }
  ];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
            className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"
          />
          <h2 className="text-xl font-semibold text-gray-800">Analyzing your meal...</h2>
          <p className="text-gray-600 mt-2">Using AI to identify nutrition content</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <div className="relative pt-12 pb-4 px-4">
        <div className="flex items-center justify-between">
          {/* Back button - always show on mobile page */}
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCancel}
            className="bg-white/80 backdrop-blur-sm rounded-full shadow-lg hover:bg-white/90"
          >
            <ArrowLeft size={20} />
          </Button>
          <h1 className="text-lg font-semibold text-gray-800">Nutrition Analysis</h1>
          {/* Spacer to center title */}
          <div className="w-10"></div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-4 space-y-4 pb-20">
        {/* Meal Image - Made taller and more square */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700"
          style={{ aspectRatio: '0.9' }}
        >
          <img
            src={mealImage}
            alt="Analyzed meal"
            className="w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute top-3 right-3 bg-black/70 text-white px-2 py-1 rounded-lg text-sm font-medium">
            {nutritionData?.confidence}% confident
          </div>
        </motion.div>

        {/* Meal Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 rounded-xl p-4"
        >
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
            {nutritionData?.mealName || "AI Analyzed Meal"}
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-sm mb-3">
            Serving size: {nutritionData?.servingSize || "1 bowl (280g)"}
          </p>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-300">Total Calories</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {nutritionData?.calories || 385}
              </p>
            </div>
            <Zap className="text-orange-500" size={28} />
          </div>
        </motion.div>

        {/* Daily Progress */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4"
        >
          <div className="flex items-center justify-between mb-3">
            <span className="text-lg font-semibold text-gray-700 dark:text-gray-300">Daily Calorie Intake</span>
            <span className="text-sm text-gray-500 dark:text-gray-400">
              {remainingCalories > 0 ? `${remainingCalories} left` : `${Math.abs(remainingCalories)} over`}
            </span>
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-300">Current: {userProfile.currentIntake} cal</span>
              <span className="text-gray-600 dark:text-gray-300">Goal: {userProfile.dailyCalorieGoal} cal</span>
            </div>
            
            <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-3">
              <div className="relative h-full">
                {/* Current intake */}
                <motion.div
                  className="bg-blue-400 h-full rounded-full"
                  initial={{ width: "0%" }}
                  animate={{ width: `${Math.min((userProfile.currentIntake / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                />
                {/* This meal addition */}
                <motion.div
                  className="bg-orange-400 h-full rounded-full absolute top-0"
                  style={{ left: `${Math.min((userProfile.currentIntake / userProfile.dailyCalorieGoal) * 100, 100)}%` }}
                  initial={{ width: "0%" }}
                  animate={{ 
                    width: `${Math.min(((nutritionData?.calories || 0) / userProfile.dailyCalorieGoal) * 100, 100 - Math.min((userProfile.currentIntake / userProfile.dailyCalorieGoal) * 100, 100))}%`
                  }}
                  transition={{ duration: 1, delay: 0.5, ease: "easeOut" }}
                />
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-blue-400 rounded-full"></div>
                  <span className="text-gray-600 dark:text-gray-300">Current</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-2 h-2 bg-orange-400 rounded-full"></div>
                  <span className="text-gray-600 dark:text-gray-300">+ This meal</span>
                </div>
              </div>
              <div className="text-sm font-medium text-gray-800 dark:text-gray-200">
                {Math.round(intakePercentage)}% of goal
              </div>
            </div>
          </div>
        </motion.div>

        {/* Macronutrients - Simple 3-column layout */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg"
        >
          <div className="flex items-center mb-3">
            <Activity size={18} className="text-blue-600 mr-2" />
            <h3 className="text-base font-semibold text-gray-800">Macronutrients</h3>
          </div>
          
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center p-3 bg-blue-50 rounded-xl">
              <div className="text-xl mb-1">💪</div>
              <div className="font-semibold text-gray-800 text-lg">{nutritionData?.protein || 0}g</div>
              <div className="text-xs text-gray-600">Protein</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-xl">
              <div className="text-xl mb-1">🌾</div>
              <div className="font-semibold text-gray-800 text-lg">{nutritionData?.carbs || 0}g</div>
              <div className="text-xs text-gray-600">Carbs</div>
            </div>
            <div className="text-center p-3 bg-orange-50 rounded-xl">
              <div className="text-xl mb-1">🥑</div>
              <div className="font-semibold text-gray-800 text-lg">{nutritionData?.fat || 0}g</div>
              <div className="text-xs text-gray-600">Fat</div>
            </div>
          </div>
        </motion.div>

        {/* Micronutrients - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg"
        >
          <h3 className="text-base font-semibold text-gray-800 mb-3">Other Nutrients</h3>
          <div className="grid grid-cols-3 gap-3">
            {microData.map((micro, index) => (
              <motion.div
                key={micro.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="text-center p-3 bg-gray-50 rounded-xl"
              >
                <div className="text-xl mb-1">{micro.icon}</div>
                <div className="font-semibold text-gray-800 text-sm">{micro.value}{micro.unit}</div>
                <div className="text-xs text-gray-600">{micro.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* AI Suggestions - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 shadow-lg border border-purple-100"
        >
          <div className="flex items-center mb-3">
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 text-white p-2 rounded-lg mr-2">
              <Zap size={16} />
            </div>
            <h3 className="text-base font-semibold text-gray-800">
              AI Nutrition Analysis
            </h3>
          </div>
          
          <div className="space-y-2">
            {(() => {
              console.log('🔍 AI Suggestions Debug:', {
                hasRealData,
                nutritionData: !!nutritionData,
                ai_suggestions: nutritionData?.ai_suggestions,
                ai_suggestions_length: nutritionData?.ai_suggestions?.length,
                ai_suggestions_content: nutritionData?.ai_suggestions || 'none',
                mealName: nutritionData?.mealName
              });
              
              if (hasRealData && nutritionData?.ai_suggestions && nutritionData.ai_suggestions.length > 0) {
                // Show real AI suggestions from the analysis
                return nutritionData.ai_suggestions.map((suggestion, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + index * 0.1 }}
                    className="flex items-start gap-2 p-2 bg-white/60 rounded-xl"
                  >
                    <div className="text-base">💡</div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{suggestion}</p>
                    </div>
                  </motion.div>
                ));
              } else {
                // Show default suggestions when no real data
                console.log('🔄 Using default AI suggestions');
                return (
                  <>
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.6 }}
                      className="flex items-start gap-2 p-2 bg-white/60 rounded-xl"
                    >
                      <div className="text-base">🥬</div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">Add more leafy greens</p>
                        <p className="text-xs text-gray-600">Your daily fiber intake could be improved</p>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.7 }}
                      className="flex items-start gap-2 p-2 bg-white/60 rounded-xl"
                    >
                      <div className="text-base">🐟</div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">Consider omega-3 rich foods</p>
                        <p className="text-xs text-gray-600">Try salmon, walnuts, or chia seeds for heart health</p>
                      </div>
                    </motion.div>
                    
                    <motion.div
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.8 }}
                      className="flex items-start gap-2 p-2 bg-white/60 rounded-xl"
                    >
                      <div className="text-base">💧</div>
                      <div>
                        <p className="text-sm font-medium text-gray-800">Stay hydrated</p>
                        <p className="text-xs text-gray-600">Drink water to aid digestion</p>
                      </div>
                    </motion.div>
                  </>
                );
              }
            })()}
          </div>
        </motion.div>

        {/* Ingredients - Compact */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg"
        >
          <h3 className="text-base font-semibold text-gray-800 mb-3">Detected Ingredients</h3>
          <div className="flex flex-wrap gap-2">
            {nutritionData?.ingredients.map((ingredient, index) => (
              <motion.span
                key={ingredient}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.05 }}
                className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium"
              >
                {ingredient}
              </motion.span>
            )) || ["Various ingredients"].map((ingredient, index) => (
              <motion.span
                key={ingredient}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.6 + index * 0.05 }}
                className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium"
              >
                {ingredient}
              </motion.span>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Render sticky buttons outside main content using portal */}
      <StickyButtons 
        isSaving={isSaving}
        handleCancel={handleCancel}
        handleSaveMeal={handleSaveMeal}
      />
    </div>
  );
};

export default NutritionAnalysis;

// Sticky Action Buttons - Outside main content, positioned like mobile navigation
const StickyButtons = ({ isSaving, handleCancel, handleSaveMeal }: { 
  isSaving: boolean; 
  handleCancel: () => void; 
  handleSaveMeal: () => void; 
}) => {
  return ReactDOM.createPortal(
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.6 }}
      className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 p-4 z-50"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 50
      }}
    >
      <div className="flex gap-3 max-w-md mx-auto">
        <Button
          variant="outline"
          onClick={handleCancel}
          className="flex-1 h-12 rounded-3xl text-base font-medium border-2"
          disabled={isSaving}
        >
          <X size={18} className="mr-2" />
          Cancel
        </Button>
        <Button
          onClick={handleSaveMeal}
          className="flex-1 h-12 rounded-3xl text-base font-medium bg-primary hover:bg-primary/90 text-primary-foreground"
          disabled={isSaving}
        >
          {isSaving ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2"
            />
          ) : (
            <Check size={18} className="mr-2" />
          )}
          {isSaving ? 'Saving...' : 'Save Meal'}
        </Button>
      </div>
    </motion.div>,
    document.body
  );
}; 